import { loadConnectAndInitialize } from '@stripe/connect-js';

import {
  createStripeAccountSession,
  getStripePublicConfig,
} from './marketplacePayments';

const connectInstancePromises = new Map();
let runtimeConfigPromise = null;

const SUPPORTED_COMPONENTS = new Set([
  'account-onboarding',
  'account-management',
  'payouts',
  'payments',
  'documents',
]);

const normalizeComponent = (component) => (
  SUPPORTED_COMPONENTS.has(component) ? component : 'account-onboarding'
);

const fetchClientSecret = async (component) => {
  const result = await createStripeAccountSession(normalizeComponent(component));
  if (!result?.client_secret) {
    throw new Error('Le prestataire de paiement n’a pas fourni de session sécurisée. Réessayez dans quelques instants.');
  }
  return result.client_secret;
};

const stripeAppearance = () => ({
  overlays: typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches
    ? 'drawer'
    : 'dialog',
  variables: {
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontSizeBase: '14px',
    spacingUnit: '6px',

    colorPrimary: '#111111',
    colorText: '#111111',
    colorSecondaryText: '#6f685f',
    colorBackground: '#ffffff',
    offsetBackgroundColor: '#f7f5f1',
    colorBorder: '#ded8cf',
    colorDanger: '#a52e27',

    formBackgroundColor: '#ffffff',
    formHighlightColorBorder: '#c0a375',
    formAccentColor: '#111111',
    formPlaceholderTextColor: '#817a71',
    formBorderRadius: '12px',
    inputFieldPaddingX: '12px',
    inputFieldPaddingY: '11px',

    borderRadius: '14px',
    buttonBorderRadius: '999px',
    buttonPaddingX: '16px',
    buttonPaddingY: '10px',
    buttonLabelFontSize: '13px',
    buttonLabelFontWeight: '700',
    buttonPrimaryColorBackground: '#111111',
    buttonPrimaryColorBorder: '#111111',
    buttonPrimaryColorText: '#ffffff',
    buttonSecondaryColorBackground: '#f1eee9',
    buttonSecondaryColorBorder: '#e0dad1',
    buttonSecondaryColorText: '#111111',

    actionPrimaryColorText: '#5f471d',
    actionPrimaryTextDecorationColor: '#5f471d',
    actionSecondaryColorText: '#4f4942',

    bodyMdFontSize: '14px',
    bodySmFontSize: '13px',
    headingXlFontSize: '22px',
    headingLgFontSize: '20px',
    headingMdFontSize: '17px',
    headingSmFontSize: '14px',
    headingXlFontWeight: '750',
    headingLgFontWeight: '750',
    headingMdFontWeight: '750',
    labelMdFontSize: '13px',
    labelMdFontWeight: '650',
    labelSmFontSize: '12px',
    labelSmFontWeight: '650',

    overlayBorderRadius: '16px',
    overlayZIndex: 1400,
    overlayBackdropColor: 'rgba(17, 17, 17, 0.34)',
  },
});


export const getStripeRuntimeConfig = async () => {
  if (!runtimeConfigPromise) {
    // The backend is authoritative for BOTH keys. Never initialize Connect.js
    // from a bundled VITE key that could belong to another Stripe platform.
    runtimeConfigPromise = getStripePublicConfig()
      .then((config) => {
        const key = String(config?.publishable_key || '').trim();
        if (!key.startsWith('pk_test_') && !key.startsWith('pk_live_')) {
          throw new Error('La configuration publique du service de paiement reçue du serveur est invalide.');
        }
        return {
          publishableKey: key,
          mode: config?.mode || (key.startsWith('pk_test_') ? 'test' : 'live'),
        };
      })
      .catch((error) => {
        runtimeConfigPromise = null;
        throw error;
      });
  }
  return runtimeConfigPromise;
};

export const getStripeConnectInstance = async (requestedComponent = 'account-onboarding') => {
  const component = normalizeComponent(requestedComponent);
  if (!connectInstancePromises.has(component)) {
    const promise = getStripeRuntimeConfig()
      .then(({ publishableKey }) => loadConnectAndInitialize({
        publishableKey,
        fetchClientSecret: () => fetchClientSecret(component),
        locale: 'fr-FR',
        appearance: stripeAppearance(),
      }))
      .catch((error) => {
        connectInstancePromises.delete(component);
        throw error;
      });
    connectInstancePromises.set(component, promise);
  }
  return connectInstancePromises.get(component);
};

export const resetStripeConnectInstance = (component = '') => {
  if (component) connectInstancePromises.delete(normalizeComponent(component));
  else connectInstancePromises.clear();
  runtimeConfigPromise = null;
};
